import { SelectionInfo } from '../types/wordTypes';
import { DocumentReader } from './DocumentReader';
import { log, logError } from '../../utils/logging';

/* global Word, Office */

export type SelectionChangeCallback = (selection: SelectionInfo) => void;
/** Notified when a selection read starts and finishes. */
export type SelectionReadStateCallback = (reading: boolean) => void;

/** Debounce live selection reads so typing does not queue a Word.run per keystroke. */
// Low enough that a click feels answered, while still collapsing the burst of
// events Word fires while a selection is being dragged. The read itself also
// coalesces (readInFlight / readPending), so a short window is safe.
const SELECTION_DEBOUNCE_MS = 180;

export type SelectionTrackerOptions = {
  /** Override the default selection debounce (ms). */
  debounceMs?: number;
};

export class SelectionTracker {
  private callbacks: SelectionChangeCallback[] = [];
  private readStateCallbacks: SelectionReadStateCallback[] = [];
  private isTracking = false;
  /**
   * Set synchronously, before addHandlerAsync is issued. `handlerRegistered`
   * only flips inside that call's async callback, so a mount/unmount/mount
   * cycle used to get past the guard twice and race two registrations against
   * each other — one of which Office rejects with an internal 5001.
   */
  private handlerSetupStarted = false;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readInFlight = false;
  private readPending = false;
  private readonly debounceMs: number;
  private readonly boundHandleSelectionChange = this.handleSelectionChange.bind(this);

  constructor(options?: SelectionTrackerOptions) {
    const ms = options?.debounceMs;
    this.debounceMs =
      typeof ms === 'number' && Number.isFinite(ms) && ms >= 0 ? ms : SELECTION_DEBOUNCE_MS;
  }

  private isSelectionHandlerAvailable(): boolean {
    return (
      typeof Office !== 'undefined' &&
      !!Office.context?.document &&
      typeof Office.context.document.addHandlerAsync === 'function' &&
      !!Office.EventType?.DocumentSelectionChanged
    );
  }

  /**
   * Starts tracking selection changes
   */
  startTracking(): void {
    if (this.isTracking) {
      return;
    }

    if (!this.isSelectionHandlerAvailable()) {
      log('Selection tracking unavailable outside a Word document host');
      return;
    }

    this.isTracking = true;
    // Register the Office handler once — stop/start only toggles isTracking so
    // remounts (e.g. sticky preference) do not stack DocumentSelectionChanged handlers.
    if (!this.handlerSetupStarted) {
      this.handlerSetupStarted = true;
      this.setupSelectionHandler();
    }
    log('Selection tracking started');
  }

  /**
   * Stops tracking selection changes
   */
  stopTracking(): void {
    this.isTracking = false;
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.readInFlight = false;
    this.readPending = false;
    this.emitReadState(false);
    log('Selection tracking stopped');
  }

  /**
   * Registers a callback for when a read starts and finishes.
   *
   * Until a read returns, the last selection is still what subscribers hold —
   * so without this the pane shows the previous paragraph as though it were
   * the one just clicked.
   */
  onReadStateChange(callback: SelectionReadStateCallback): () => void {
    this.readStateCallbacks.push(callback);
    return () => {
      const index = this.readStateCallbacks.indexOf(callback);
      if (index > -1) {
        this.readStateCallbacks.splice(index, 1);
      }
    };
  }

  private emitReadState(reading: boolean): void {
    this.readStateCallbacks.forEach((callback) => {
      try {
        callback(reading);
      } catch (error) {
        logError('Error in selection read-state callback', error);
      }
    });
  }

  /**
   * Registers a callback for selection changes
   */
  onSelectionChange(callback: SelectionChangeCallback): () => void {
    this.callbacks.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.callbacks.indexOf(callback);
      if (index > -1) {
        this.callbacks.splice(index, 1);
      }
    };
  }

  /**
   * Gets the current selection immediately (full read, including paragraph index).
   */
  async getCurrentSelection(): Promise<SelectionInfo> {
    return await DocumentReader.getSelectionOrParagraph();
  }

  private setupSelectionHandler(): void {
    if (!this.isSelectionHandlerAvailable()) {
      log('Selection handler setup skipped because Office document API is unavailable');
      return;
    }

    // Use Office.js event handler for selection changes
    // @ts-ignore - Office.context is available at runtime
    Office.context.document.addHandlerAsync(
      Office.EventType.DocumentSelectionChanged,
      this.boundHandleSelectionChange,
      (result: Office.AsyncResult<void>) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          // Allow a later startTracking to try again rather than leaving the
          // pane permanently deaf to selection changes.
          this.handlerSetupStarted = false;
          logError('Failed to setup selection handler', result.error);
        } else {
          log('Selection handler setup complete');
        }
      }
    );
  }

  private handleSelectionChange(): void {
    if (!this.isTracking) return;

    // Announce the read before the debounce, not after it. Subscribers still
    // hold the previous paragraph, and the point of the signal is to stop them
    // showing it the moment the user moves — not 180ms later.
    this.emitReadState(true);

    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      void this.processSelectionChange();
    }, this.debounceMs);
  }

  private emit(selection: SelectionInfo): void {
    if (!this.isTracking) return;
    this.callbacks.forEach((callback) => {
      try {
        callback(selection);
      } catch (error) {
        logError('Error in selection change callback', error);
      }
    });
  }

  private async processSelectionChange(): Promise<void> {
    if (!this.isTracking) return;

    if (this.readInFlight) {
      this.readPending = true;
      return;
    }

    this.readInFlight = true;
    try {
      // Lightweight read: current selection/paragraph only — never scan the full body.
      //
      // Emitted in two phases. The plain text arrives after one round trip and
      // is published straight away; the OOXML package behind track-changes
      // rendering costs a second, much heavier round trip and lands after. A
      // single emit at the end left the pane blank for the whole read.
      const selection = await DocumentReader.getSelectionLight((partial) => {
        this.emit(partial);
      });

      this.emit(selection);
    } catch (error) {
      logError('Error handling selection change', error);
    } finally {
      this.readInFlight = false;
      if (this.readPending) {
        this.readPending = false;
        void this.processSelectionChange();
      } else {
        this.emitReadState(false);
      }
    }
  }
}
